import { sdkTypes } from '@vansite/ts-sharetribe-flex-sdk';
import { validate } from 'uuid';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchTranslations } from '../../ducks/translations.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { getLocaleFromPath } from '../../util/locale';
import { logError } from '../../util/log';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SHOW_USER_REQUEST = 'app/ProfilePage/SHOW_USER_REQUEST';
export const SHOW_USER_SUCCESS = 'app/ProfilePage/SHOW_USER_SUCCESS';
export const SHOW_USER_ERROR = 'app/ProfilePage/SHOW_USER_ERROR';

export const QUERY_LISTINGS_REQUEST = 'app/ProfilePage/QUERY_LISTINGS_REQUEST';
export const QUERY_LISTINGS_SUCCESS = 'app/ProfilePage/QUERY_LISTINGS_SUCCESS';
export const QUERY_LISTINGS_ERROR = 'app/ProfilePage/QUERY_LISTINGS_ERROR';

export const QUERY_REVIEWS_REQUEST = 'app/ProfilePage/QUERY_REVIEWS_REQUEST';
export const QUERY_REVIEWS_SUCCESS = 'app/ProfilePage/QUERY_REVIEWS_SUCCESS';
export const QUERY_REVIEWS_ERROR = 'app/ProfilePage/QUERY_REVIEWS_ERROR';

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.ProfilePage} */
const initialState = {
  userId: null,
  listings: [],
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

export default function profilePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SHOW_USER_REQUEST:
      return { ...state, userShowError: null, userId: payload };
    case SHOW_USER_SUCCESS:
      return state;
    case SHOW_USER_ERROR:
      return { ...state, userShowError: payload };

    case QUERY_LISTINGS_REQUEST:
      return {
        ...state,

        // Empty listings only when user id changes
        listings: payload === state.userId ? state.listings : [],

        queryListingsError: null,
      };
    case QUERY_LISTINGS_SUCCESS:
      return { ...state, listings: payload };
    case QUERY_LISTINGS_ERROR:
      return { ...state, listings: [], queryListingsError: payload };
    case QUERY_REVIEWS_REQUEST:
      return { ...state, queryReviewsError: null };
    case QUERY_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case QUERY_REVIEWS_ERROR:
      return { ...state, reviews: [], queryReviewsError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const showUserRequest = (userId) => ({
  type: SHOW_USER_REQUEST,
  payload: userId,
});

export const showUserSuccess = () => ({
  type: SHOW_USER_SUCCESS,
});

export const showUserError = (e) => ({
  type: SHOW_USER_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = (userId) => ({
  type: QUERY_LISTINGS_REQUEST,
  payload: userId,
});

export const queryListingsSuccess = (listings) => ({
  type: QUERY_LISTINGS_SUCCESS,
  payload: listings,
});

export const queryListingsError = (e) => ({
  type: QUERY_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryReviewsRequest = () => ({
  type: QUERY_REVIEWS_REQUEST,
});

export const queryReviewsSuccess = (reviews) => ({
  type: QUERY_REVIEWS_SUCCESS,
  payload: reviews,
});

export const queryReviewsError = (e) => ({
  type: QUERY_REVIEWS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const queryUserListings = (userId, locale) => async (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(userId));
  try {
    const response = await sdk.listings.query({
      authorId: userId,
      include: ['author', 'images'],
      'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
    });
    const listings = denormalisedResponseEntities(response);

    if (listings.length > 0)
      dispatch(fetchTranslations({ listing: { ids: listings.map((l) => l.id?.uuid) } }, locale));
    dispatch(addMarketplaceEntities(response));
    // Pick only the id and type properties from the response listings
    const listingIds = response.data.data.map(l => l.id);
    dispatch(queryListingsSuccess(listingIds));
    return response;
  } catch (e) {
    logError(e, 'show-user-failed', { userId: userId.uuid });
    dispatch(queryListingsError(storableError(e)));
    throw e;
  }
};

export const queryUserReviews = (userId, locale) => async (dispatch, getState, sdk) => {
  dispatch(queryReviewsRequest());
  try {
    const response = await sdk.reviews.query({
      subjectId: userId,
      state: 'public',
      include: ['author', 'author.profileImage', 'listing'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    });
    const reviews = denormalisedResponseEntities(response);
    if (reviews.length > 0)
      dispatch(fetchTranslations({ review: { ids: reviews.map((r) => r.id?.uuid) } }, locale));
    dispatch(addMarketplaceEntities(response));
    const reviewIds = response.data.data.map(r => r.id);
    dispatch(queryReviewsSuccess(reviewIds));
  } catch (e) {
    logError(e, 'show-user-reviews-failed', { userId: userId.uuid });
    dispatch(queryReviewsError(e));
  }
};

export const showUser = (userId) => async (dispatch, getState, sdk) => {
  dispatch(showUserRequest(userId));
  try {
    const response = await sdk.users.show({
      id: userId,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    });
    dispatch(addMarketplaceEntities(response));
    dispatch(showUserSuccess());
    return response;
  } catch (e) {
    logError(e, 'show-user-failed', { userId: userId.uuid });
    dispatch(showUserError(storableError(e)));
  }
};

export const loadData = (params, search, match) => (dispatch) => {
  const locale = getLocaleFromPath(match.path);
  const userId = new UUID(params.id);

  if (!validate(userId.uuid)) {
    dispatch(showUserError(storableError({
      'name': 'AxiosError',
      'message': 'Request failed with status code 404',
      'status': 404,
      'statusText': 'ERR_BAD_REQUEST',
      'data': {
        'errors': [{
          'id': new UUID().uuid,
          'status': 404,
          'code': 'not-found',
          'title': 'Not found.',
        }],
      },
    })));
    return Promise.resolve();
  }

  return Promise.all([
    dispatch(showUser(userId)),
    dispatch(fetchCurrentUser()),
    dispatch(fetchTranslations({ user: { ids: [userId?.uuid] } }, locale)),
    dispatch(queryUserListings(userId, locale)),
    dispatch(queryUserReviews(userId, locale)),
  ]);
};
